import { useCallback, useMemo, useState } from "react"
import {
  buildContactConsentConditionPatch,
  getContactConsentFromJourneyData,
  mergeContactConsentIntoJourneyData,
  shouldShowBiContactConsentPopup,
} from "../services/contactConsentPolicy"

/**
 * Reusable BI proceed gate for IRDAI contact/consent capture.
 *
 * Usage in any BI component:
 *   const gate = useBiContactConsentGate({ journeyState, setJourneyState, setConditionState, saveApplication, config })
 *   const handleProceedClick = (ev) => {
 *     ev.preventDefault()
 *     if (!isChecked) return
 *     gate.requestProceed(ev, () => skipPayment ? handleProceedSkipPayment(ev) : handleProceed(ev))
 *   }
 */
export default function useBiContactConsentGate({
  journeyState,
  setJourneyState,
  setConditionState,
  saveApplication,
  config,
  buildSavePayload,
}) {
  const [showModal, setShowModal] = useState(false)
  const [pendingProceed, setPendingProceed] = useState(null)

  const initialValues = useMemo(
    () => getContactConsentFromJourneyData(journeyState?.journeyData, config),
    [journeyState?.journeyData, config, showModal]
  )

  const requestProceed = useCallback(
    (event, proceedFn) => {
      const snapshot = getContactConsentFromJourneyData(
        journeyState?.journeyData,
        config
      )

      if (shouldShowBiContactConsentPopup(snapshot)) {
        setPendingProceed(() => proceedFn)
        setShowModal(true)
        return
      }

      proceedFn(event)
    },
    [journeyState?.journeyData, config]
  )

  const handleModalClose = useCallback(() => {
    setShowModal(false)
    setPendingProceed(null)
  }, [])

  const handleModalProceed = useCallback(
    async values => {
      const updatedJourneyData = mergeContactConsentIntoJourneyData(
        journeyState?.journeyData,
        values,
        config
      )

      setJourneyState(prev => ({
        ...prev,
        journeyData: updatedJourneyData,
      }))

      setConditionState(prev => ({
        ...prev,
        ...buildContactConsentConditionPatch(config, values),
      }))

      if (saveApplication && buildSavePayload) {
        try {
          await saveApplication(buildSavePayload(updatedJourneyData, values))
        } catch (error) {
          console.error("Failed to save contact consent details", error)
          return
        }
      }

      setShowModal(false)
      const proceedFn = pendingProceed
      setPendingProceed(null)
      if (proceedFn) {
        proceedFn({ preventDefault: () => {} })
      }
    },
    [
      journeyState?.journeyData,
      config,
      setJourneyState,
      setConditionState,
      saveApplication,
      buildSavePayload,
      pendingProceed,
    ]
  )

  return {
    showModal,
    initialValues,
    requestProceed,
    handleModalClose,
    handleModalProceed,
  }
}
